import { ConflictError } from "../shared/errors";
import { MAX_POSTGRES_INTEGER } from "../shared/postgres-integer";
import type { Order, OrderStatus } from "./types";

/**
 * The fewest cents a fulfiller can enter as what StockX charged for a line. 0 is refused: a buy
 * costs something, and a paid cost of 0 would refund the shopper the whole price.
 */
export const MIN_PAID_COST_CENTS = 1;

/** The most: `order_lines.paid_cost_cents` is a Postgres `integer`. */
export const MAX_PAID_COST_CENTS = MAX_POSTGRES_INTEGER;

/** Max active lines per physical ship bag — matches boxed `SHIPPING_MAX_CARDS`. */
export const ORDER_BAG_MAX_LINES = 15;

/**
 * Whether a full bag moves on to PROCESSING by itself. A bag holding a StockX line stays PENDING,
 * because PROCESSING is where the fulfiller enters what StockX charged for it
 * (`stockx-size-refund.ts`), and only their move to PROCESSING carries that amount.
 */
export function shouldFullBagStartProcessing({
  lineCount,
  hasStockxLine,
  maxLines = ORDER_BAG_MAX_LINES,
}: {
  lineCount: number;
  hasStockxLine: boolean;
  maxLines?: number;
}): boolean {
  return lineCount >= maxLines && !hasStockxLine;
}

/**
 * Reject over-cap creates; flip a full PENDING bag to PROCESSING at create time, unless it holds
 * a StockX line ({@link shouldFullBagStartProcessing}).
 */
export function resolveCreateBagStatus(args: {
  lineCount: number;
  status: OrderStatus;
  hasStockxLine: boolean;
  maxLines?: number;
}): OrderStatus {
  const maxLines = args.maxLines ?? ORDER_BAG_MAX_LINES;
  if (args.lineCount > maxLines) {
    throw new ConflictError(`Order would exceed ${maxLines} active lines`);
  }
  if (args.status === "PENDING" && shouldFullBagStartProcessing({ ...args, maxLines })) {
    return "PROCESSING";
  }
  return args.status;
}

/**
 * Default initial status: PENDING for anything somebody then fulfils, UNAPPROVED for the two
 * digital types that need an approval first.
 *
 * A physical withdrawal (`WITHDRAW_ASSET`) is PENDING, not UNAPPROVED: both Job API handlers
 * require PROCESSING, and the state machine has no UNAPPROVED -> PROCESSING edge, so an
 * UNAPPROVED withdrawal could never be dispatched. Staff review is the explicit HOLD the
 * withdrawal route passes instead.
 */
export function initialOrderStatus(type: Order["type"]): OrderStatus {
  switch (type) {
    case "WITHDRAW_STEAM":
    case "SELL_FOR_CRYPTO":
      return "UNAPPROVED";
    default:
      return "PENDING";
  }
}
